/* Textur-Modus (Werkzeug „Textur“): Ziel wählen, Textur anwenden, ausrichten, fein einstellen und mit dem Pinsel anmalen. */
import Cmd from '../../model/cmds.ts';
import Core from '../../core.ts';
import TexAlign from '../../model/texalign.ts';
import type { FacePatch } from '../../model/texalign.ts';
import type { Brush } from '../../model/types.ts';
import { useT } from '../hooks.ts';
import type { TexApp, TexShared } from './types.ts';
import { CommitInput, NumInput, TexCanvas } from './widgets.tsx';

type Run = (fn: (b: Brush, i: number) => FacePatch | null, label: string) => void;

export function TexMode(props: { app: TexApp; shared: TexShared; refresh: () => void }) {
  const t = useT();
  const { app, shared } = props;
  const A = shared.align, T = app.texPaint, nb = app.sel.b.size, nf = app.faceSel.length;
  const faces = nf ? app.faceSel.map((x) => ({ b: x.b, f: x.f })) : null;
  const sz = (b: Brush, i: number) => app.wads.size(b.faces[i].tex) || { w: 64, h: 64 };
  const run: Run = (fn, label) => {
    if (!nb) { app.status(t('tm.need_target')); return; }
    const d = TexAlign.apply(app.doc, app.sel.b, faces, fn);
    if (d === app.doc) { app.status(t('pn.nothing_to_change')); return; }
    app.commit(d, label);
  };
  const edge = (e: 'left' | 'right' | 'top' | 'bottom' | 'hcenter' | 'vcenter', key: string, tip: string) => (
    <button className="btn sm" title={t(tip)} onClick={() => run((b, i) => TexAlign.alignEdge(b, i, e, sz(b, i).w, sz(b, i).h), t('cmd.tex_edge'))}>{t(key)}</button>
  );
  const b1 = nb === 1 ? Cmd.selBrushes(app.doc, app.sel)[0] : null;
  const cur = new Set(app.faceSel.map((x) => x.b + ':' + x.f));
  const pickFace = (id: string, i: number, multi: boolean) => {
    const k = id + ':' + i, l = app.faceSel.slice(), at = l.findIndex((x) => x.b + ':' + x.f === k);
    if (multi) { if (at >= 0) l.splice(at, 1); else l.push({ b: id, f: i }); app.setFaceSel(l); } else app.setFaceSel([{ b: id, f: i }]);
    if (app.v3) app.v3.setSel();
  };
  const move = (label: string, du: number, dv: number) => run((b, i) => TexAlign.shift(b, i, du, dv), label);

  return (
    <div id="tex-mode">
      <h3>{t('tm.title')}</h3>
      <p className="small">{nb ? (nf ? t('tm.target_faces', { count: nf }) : t('tm.target_brushes', { count: nb })) : t('tm.no_target')}</p>
      {/* Flächenliste: einen Brush gewählt, dann eine Oberfläche direkt wählen (auch aus 2D); Umschalt/Strg fügt hinzu */}
      {b1 && (
        <div className="chips">
          {b1.faces.map((f, i) => (
            <button key={i} className={'chip' + (cur.has(b1.id + ':' + i) ? ' act' : '')} title={f.tex}
              onClick={(e) => pickFace(b1.id, i, e.shiftKey || e.ctrlKey || e.metaKey)}>{(i + 1) + ' ' + TexAlign.dirName(Core.faceNormal(b1.verts, f.vi))}</button>
          ))}
        </div>
      )}
      <div className="row" style={{ flexWrap: 'nowrap', alignItems: 'flex-start' }}>
        <TexCanvas name={app.activeTex} wads={app.wads} version={app.wadNames.join('|')} className="preview-tex" style={{ width: 48, height: 48 }} />
        <div style={{ display: 'grid', gap: 6, minWidth: 0, flex: 1 }}>
          <CommitInput value={app.activeTex} list="dl-tex" aria-label={t('pn.active_tex')} onCommit={(v) => { const n = v.trim(); if (n) app.setActiveTex(n, false); }} />
          <div className="row">
            <button className="btn sm primary" title={t('tm.apply_tip')} onClick={() => { if (!app.applyTex(app.activeTex)) app.status(t('tm.need_target')); }}>{t('tm.apply')}</button>
            <button className="btn sm" title={t('tm.pick_face_tip')} onClick={() => { const sf = app.faceSel.length === 1 ? app.faceSel[0] : null; if (!sf) { app.status(t('pn.pick_single')); return; } app.pickTexture(sf.b, sf.f); }}>{t('tm.pick_face')}</button>
          </div>
        </div>
      </div>

      <h3>{t('tm.align')}</h3>
      <div className="row">{edge('left', 'tm.left', 'tm.left_tip')}{edge('hcenter', 'tm.hcenter', 'tm.hcenter_tip')}{edge('right', 'tm.right', 'tm.right_tip')}{edge('top', 'tm.top', 'tm.top_tip')}{edge('vcenter', 'tm.vcenter', 'tm.vcenter_tip')}{edge('bottom', 'tm.bottom', 'tm.bottom_tip')}</div>
      <div className="row">
        <span className="lbl-s">{t('pn.fit')}</span>
        <NumInput value={A.tx} width="52px" aria-label="X" onChange={(v) => { A.tx = Math.max(0.01, v); }} />
        <span className="lbl-s">×</span>
        <NumInput value={A.ty} width="52px" aria-label="Y" onChange={(v) => { A.ty = Math.max(0.01, v); }} />
        <button className="btn sm" title={t('pn.fit_tip')} onClick={() => run((b, i) => TexAlign.fit(b, i, A.tx, A.ty, sz(b, i).w, sz(b, i).h), t('cmd.tex_fit'))}>{t('pn.fit_btn')}</button>
        <button className="btn sm" title={t('pn.reset_world_tip')} onClick={() => run((b, i) => TexAlign.reset(b, i), t('cmd.tex_reset'))}>{t('pn.reset_world')}</button>
      </div>

      <details className="gen" open={shared.texFineOpen !== false} onToggle={(e) => { shared.texFineOpen = (e.currentTarget as HTMLDetailsElement).open; }}>
        <summary>{t('tm.fine')}</summary>
        <div className="body">
          <div className="row">
            <span className="lbl-s">{t('pn.offset')}</span>
            <NumInput value={A.step} width="56px" aria-label={t('pn.offset')} onChange={(v) => { A.step = v; }} />
            <button className="btn sm" onClick={() => move(t('cmd.tex_shift'), -A.step, 0)}>←</button>
            <button className="btn sm" onClick={() => move(t('cmd.tex_shift'), A.step, 0)}>→</button>
            <button className="btn sm" onClick={() => move(t('cmd.tex_shift'), 0, -A.step)}>↑</button>
            <button className="btn sm" onClick={() => move(t('cmd.tex_shift'), 0, A.step)}>↓</button>
          </div>
          <div className="row">
            <span className="lbl-s">{t('pn.scale')}</span>
            <button className="btn sm" onClick={() => run((b, i) => TexAlign.scale(b, i, 2), t('cmd.tex_scale'))}>×2</button>
            <button className="btn sm" onClick={() => run((b, i) => TexAlign.scale(b, i, 0.5), t('cmd.tex_scale'))}>÷2</button>
            <span className="lbl-s">{t('cmd.mirror')}</span>
            <button className="btn sm" onClick={() => run((b, i) => TexAlign.flipU(b, i), t('cmd.tex_mirror'))}>U</button>
            <button className="btn sm" onClick={() => run((b, i) => TexAlign.flipV(b, i), t('cmd.tex_mirror'))}>V</button>
          </div>
          <div className="row">
            <span className="lbl-s">{t('pn.rotate')}</span>
            <button className="btn sm" onClick={() => run((b, i) => TexAlign.rotate(b, i, -90), t('cmd.tex_rotate'))}>−90°</button>
            <button className="btn sm" onClick={() => run((b, i) => TexAlign.rotate(b, i, 90), t('cmd.tex_rotate'))}>+90°</button>
            <NumInput value={A.deg} width="56px" aria-label={t('pn.rotate')} onChange={(v) => { A.deg = v; }} />
            <button className="btn sm" title={t('pn.rotate_tip')} onClick={() => run((b, i) => TexAlign.rotate(b, i, A.deg), t('cmd.tex_rotate'))}>↺</button>
            <button className="btn sm" onClick={() => run((b, i) => TexAlign.rotate(b, i, -A.deg), t('cmd.tex_rotate'))}>↻</button>
          </div>
        </div>
      </details>

      <h3>{t('tm.brush')}</h3>
      <p className="small">{T.src ? t('tm.src', { tex: T.src.attrs.tex, xs: T.src.attrs.xs, ys: T.src.attrs.ys }) : t('tm.src_none')}</p>
      <div className="row">
        <button className={'btn sm' + (app.texMode === 'paint' ? ' primary' : '')} onClick={() => app.setTexMode(app.texMode === 'paint' ? 'edit' : 'paint')}>{app.texMode === 'paint' ? t('tm.paint_off') : t('tm.paint_on')}</button>
        {T.src && <button className="btn sm" onClick={() => { T.src = null; app.renderOpts(); props.refresh(); }}>{t('tm.src_clear')}</button>}
      </div>
      <p className="small">{t('tm.paint_note')}</p>
    </div>
  );
}
